import { t, Trans } from "@lingui/macro";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useKey } from "react-use";
import { zeroAddress } from "viem";

import { BASIS_POINTS_DIVISOR, DEFAULT_ALLOWED_SWAP_SLIPPAGE_BPS, USD_DECIMALS } from "config/factors";
import { useSettings } from "context/SettingsContext/SettingsContextProvider";
import { usePositionsConstants, useUserReferralInfo } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { useMarketInfo } from "context/SyntheticsStateContext/hooks/marketHooks";
import {
  useOrderEditorIsSubmittingState,
  useOrderEditorSizeInputValueState,
  useOrderEditorTriggerPriceInputValueState,
  useOrderEditorTriggerRatioInputValueState,
} from "context/SyntheticsStateContext/hooks/orderEditorHooks";
import { selectMarketsInfoData, selectTokensData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import {
  selectOrderEditorAcceptablePrice,
  selectOrderEditorAcceptablePriceImpactBps,
  selectOrderEditorDecreaseAmounts,
  selectOrderEditorDefaultAllowedSwapSlippageBps,
  selectOrderEditorExecutionFee,
  selectOrderEditorExistingPosition,
  selectOrderEditorFindSwapPath,
  selectOrderEditorFromToken,
  selectOrderEditorIncreaseAmounts,
  selectOrderEditorInitialAcceptablePriceImpactBps,
  selectOrderEditorIsRatioInverted,
  selectOrderEditorMarkRatio,
  selectOrderEditorMaxAllowedLeverage,
  selectOrderEditorMinOutputAmount,
  selectOrderEditorNextPositionValuesForIncrease,
  selectOrderEditorNextPositionValuesWithoutPnlForIncrease,
  selectOrderEditorPositionOrderError,
  selectOrderEditorPriceImpactFeeBps,
  selectOrderEditorSelectedAllowedSwapSlippageBps,
  selectOrderEditorSetAcceptablePriceImpactBps,
  selectOrderEditorSetDefaultAllowedSwapSlippageBps,
  selectOrderEditorSetSelectedAllowedSwapSlippageBps,
  selectOrderEditorSizeDeltaUsd,
  selectOrderEditorTotalSwapImpactBps,
  selectOrderEditorTriggerPrice,
  selectOrderEditorTriggerRatio,
} from "context/SyntheticsStateContext/selectors/orderEditorSelectors";
import { useCalcSelector, useSelector } from "context/SyntheticsStateContext/utils";
import { useExpressOrdersParams } from "domain/synthetics/express/useRelayerFeeHandler";
import {
  getExpressParamsForSubmit,
  reportMultichainExpressSubmitError,
} from "domain/synthetics/express/validateMultichainExpressSubmit";
import useUiFeeFactorRequest from "domain/synthetics/fees/utils/useUiFeeFactor";
import {
  EditingOrderSource,
  OrderInfo,
  PositionOrderInfo,
  SwapOrderInfo,
  isIncreaseOrderType,
  isLimitDecreaseOrderType,
  isLimitIncreaseOrderType,
  isLimitSwapOrderType,
  isStopIncreaseOrderType,
  isStopLossOrderType,
  isSwapOrderType,
  isTriggerDecreaseOrderType,
} from "domain/synthetics/orders";
import { sendBatchOrderTxn } from "domain/synthetics/orders/sendBatchOrderTxn";
import { useOrderTxnCallbacks } from "domain/synthetics/orders/useOrderTxnCallbacks";
import {
  formatAcceptablePrice,
  formatLeverage,
  formatLiquidationPrice,
  getNameByOrderType,
  substractMaxLeverageSlippage,
} from "domain/synthetics/positions";
import { convertToTokenAmount, convertToUsd, getTokenData } from "domain/synthetics/tokens";
import {
  getIncreasePositionAmounts,
  getMarkPrice,
  getNextPositionValuesForIncreaseTrade,
} from "domain/synthetics/trade";
import { useCloseSizeInput } from "domain/synthetics/trade/useCloseSizeInput";
import { getExpressError, getIsMaxLeverageExceeded } from "domain/synthetics/trade/utils/validation";
import { TokensRatioAndSlippage } from "domain/tokens";
import {
  FULL_POSITION_CLOSE_SIZE_DELTA_USD,
  getPositionCloseSizeDeltaUsdForDisplay,
  isFullPositionCloseSizeDeltaUsd,
} from "domain/tpsl/utils";
import { numericBinarySearch } from "lib/binarySearch";
import { useChainId } from "lib/chains";
import { useMultipleWalletExtensionsChainError } from "lib/chains/getMultipleWalletExtensionsChainError";
import { helperToast } from "lib/helperToast";
import {
  calculateDisplayDecimals,
  formatAmount,
  formatAmountFree,
  formatBalanceAmount,
  formatDeltaUsd,
  formatTokenAmountWithUsd,
  formatUsd,
  formatUsdPrice,
  parseValue,
} from "lib/numbers";
import { getByKey } from "lib/objects";
import { useJsonRpcProvider } from "lib/rpc";
import { getPageOutdatedError, useHasOutdatedUi } from "lib/useHasOutdatedUi";
import { sendEditOrderEvent } from "lib/userAnalytics";
import useWallet from "lib/wallets/useWallet";
import { bigMath } from "sdk/utils/bigmath";
import { BatchOrderTxnParams, buildUpdateOrderPayload } from "sdk/utils/orderTransactions";

import { AcceptablePriceImpactInputRow } from "components/AcceptablePriceImpactInputRow/AcceptablePriceImpactInputRow";
import Button from "components/Button/Button";
import BuyInputSection from "components/BuyInputSection/BuyInputSection";
import ExternalLink from "components/ExternalLink/ExternalLink";
import Modal from "components/Modal/Modal";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";
import { MarginPercentageSlider } from "components/TradeboxMarginFields/MarginPercentageSlider";
import { ValueTransition } from "components/ValueTransition/ValueTransition";

import SpinnerIcon from "img/ic_spinner.svg?react";

import { AllowedSwapSlippageInputRow } from "../AllowedSwapSlippageInputRowImpl/AllowedSwapSlippageInputRowImpl";
import { SyntheticsInfoRow } from "../SyntheticsInfoRow";
import { ExpressTradingWarningCard } from "../TradeBox/ExpressTradingWarningCard";

import "./OrderEditor.scss";

type Props = {
  order: OrderInfo;
  source: EditingOrderSource;
  onClose: () => void;
  onBack?: () => void;
};

export function OrderEditor(p: Props) {
  const { chainId, srcChainId } = useChainId();
  const { signer } = useWallet();
  const { provider } = useJsonRpcProvider(chainId);
  const tokensData = useSelector(selectTokensData);
  const hasOutdatedUi = useHasOutdatedUi();
  const multipleWalletExtensionsChainError = useMultipleWalletExtensionsChainError();
  const marketsInfoData = useSelector(selectMarketsInfoData);
  const { makeOrderTxnCallback } = useOrderTxnCallbacks();
  const [isSubmitting, setIsSubmitting] = useOrderEditorIsSubmittingState();

  const [sizeInputValue, setSizeInputValue] = useOrderEditorSizeInputValueState();
  const [triggerPriceInputValue, setTriggerPriceInputValue] = useOrderEditorTriggerPriceInputValueState();
  const [triggerRatioInputValue, setTriggerRatioInputValue] = useOrderEditorTriggerRatioInputValueState();
  const [isInited, setIsInited] = useState(false);

  const calcSelector = useCalcSelector();

  const sizeDeltaUsd = useSelector(selectOrderEditorSizeDeltaUsd);
  const triggerPrice = useSelector(selectOrderEditorTriggerPrice);
  const fromToken = useSelector(selectOrderEditorFromToken);
  const markRatio = useSelector(selectOrderEditorMarkRatio);
  const isRatioInverted = useSelector(selectOrderEditorIsRatioInverted);
  const triggerRatio = useSelector(selectOrderEditorTriggerRatio);
  const minOutputAmount = useSelector(selectOrderEditorMinOutputAmount);

  const market = useMarketInfo(p.order.marketAddress);
  const indexToken = getTokenData(tokensData, market?.indexTokenAddress);
  const markPrice = indexToken?.prices
    ? getMarkPrice({
        prices: indexToken.prices,
        isIncrease: isIncreaseOrderType(p.order.orderType),
        isLong: p.order.isLong,
      })
    : undefined;
  const existingPosition = useSelector(selectOrderEditorExistingPosition);

  const executionFee = useSelector(selectOrderEditorExecutionFee);

  const additionalExecutionFee = useMemo(() => {
    if (!executionFee || p.order.executionFee >= executionFee.feeTokenAmount) {
      return undefined;
    }

    const feeTokenData = getTokenData(tokensData, executionFee.feeToken.address);
    const additionalTokenAmount = executionFee.feeTokenAmount - p.order.executionFee;

    return {
      feeUsd: convertToUsd(additionalTokenAmount, executionFee.feeToken.decimals, feeTokenData?.prices.minPrice),
      feeTokenAmount: additionalTokenAmount,
      feeToken: executionFee.feeToken,
    };
  }, [executionFee, tokensData, p.order.executionFee]);

  const positionOrder = p.order as PositionOrderInfo | undefined;
  const positionIndexToken = positionOrder?.indexToken;
  const indexTokenAmount = useMemo(
    () =>
      positionIndexToken ? convertToTokenAmount(sizeDeltaUsd, positionIndexToken.decimals, triggerPrice) : undefined,
    [positionIndexToken, sizeDeltaUsd, triggerPrice]
  );
  const nextPositionValuesForIncrease = useSelector(selectOrderEditorNextPositionValuesForIncrease);
  const nextPositionValuesWithoutPnlForIncrease = useSelector(selectOrderEditorNextPositionValuesWithoutPnlForIncrease);

  const isTriggerDecrease = isTriggerDecreaseOrderType(p.order.orderType);

  const closeSize = useCloseSizeInput({
    positionSizeInUsd: existingPosition?.sizeInUsd,
    positionSizeInTokens: existingPosition?.sizeInTokens,
    indexTokenDecimals: positionIndexToken?.decimals ?? 18,
    indexTokenSymbol: positionIndexToken?.symbol ?? "",
    onCloseSizeUsdChange: isTriggerDecrease ? setSizeInputValue : undefined,
  });

  const findSwapPath = useSelector(selectOrderEditorFindSwapPath);

  const userReferralInfo = useUserReferralInfo();
  const { uiFeeFactor } = useUiFeeFactorRequest(chainId);

  const acceptablePrice = useSelector(selectOrderEditorAcceptablePrice);
  const acceptablePriceImpactBps = useSelector(selectOrderEditorAcceptablePriceImpactBps);
  const initialAcceptablePriceImpactBps = useSelector(selectOrderEditorInitialAcceptablePriceImpactBps);
  const setAcceptablePriceImpactBps = useSelector(selectOrderEditorSetAcceptablePriceImpactBps);
  const increaseAmounts = useSelector(selectOrderEditorIncreaseAmounts);
  const maxAllowedLeverage = useSelector(selectOrderEditorMaxAllowedLeverage);

  const defaultAllowedSwapSlippageBps = useSelector(selectOrderEditorDefaultAllowedSwapSlippageBps);
  const setDefaultAllowedSwapSlippageBps = useSelector(selectOrderEditorSetDefaultAllowedSwapSlippageBps);
  const selectedAllowedSwapSlippageBps = useSelector(selectOrderEditorSelectedAllowedSwapSlippageBps);
  const setSelectedAllowedSwapSlippageBps = useSelector(selectOrderEditorSetSelectedAllowedSwapSlippageBps);
  const swapImpactBps = useSelector(selectOrderEditorTotalSwapImpactBps);

  const decreaseAmounts = useSelector(selectOrderEditorDecreaseAmounts);
  const { minCollateralUsd } = usePositionsConstants();

  const recommendedAcceptablePriceImpactBps =
    isLimitIncreaseOrderType(p.order.orderType) && increaseAmounts?.acceptablePrice !== undefined
      ? increaseAmounts.recommendedAcceptablePriceDeltaBps
      : decreaseAmounts?.recommendedAcceptablePriceDeltaBps !== undefined
        ? bigMath.abs(decreaseAmounts?.recommendedAcceptablePriceDeltaBps)
        : undefined;

  const priceImpactFeeBps = useSelector(selectOrderEditorPriceImpactFeeBps);

  const isMaxLeverageError = useMemo(() => {
    if (isLimitIncreaseOrderType(p.order.orderType) && sizeDeltaUsd !== undefined) {
      if (nextPositionValuesWithoutPnlForIncrease?.nextLeverage === undefined) {
        return false;
      }

      const positionOrder = p.order as PositionOrderInfo;
      return getIsMaxLeverageExceeded(
        nextPositionValuesWithoutPnlForIncrease?.nextLeverage,
        positionOrder.marketInfo,
        positionOrder.isLong,
        sizeDeltaUsd
      );
    }
    return false;
  }, [p.order, sizeDeltaUsd, nextPositionValuesWithoutPnlForIncrease?.nextLeverage]);

  const { savedAcceptablePriceImpactBuffer, isSetAcceptablePriceImpactEnabled } = useSettings();

  const detectAndSetAvailableMaxLeverage = useCallback(() => {
    const positionOrder = p.order as PositionOrderInfo;
    const marketInfo = positionOrder.marketInfo;
    const collateralToken = positionOrder.targetCollateralToken;

    if (!positionIndexToken || !fromToken || minCollateralUsd === undefined) return;

    const { returnValue: newSizeDeltaUsd } = numericBinarySearch<bigint | undefined>(
      1,
      // "10 *" means we do 1..50 search but with 0.1x step
      (10 * maxAllowedLeverage) / BASIS_POINTS_DIVISOR,
      (lev) => {
        const leverage = BigInt((lev / 10) * BASIS_POINTS_DIVISOR);
        const increaseAmounts = getIncreasePositionAmounts({
          collateralToken,
          findSwapPath,
          indexToken: positionIndexToken,
          indexTokenAmount,
          initialCollateralAmount: positionOrder.initialCollateralDeltaAmount,
          initialCollateralToken: fromToken,
          isLong: positionOrder.isLong,
          marketInfo: positionOrder.marketInfo,
          position: existingPosition,
          strategy: "leverageByCollateral",
          uiFeeFactor,
          userReferralInfo,
          acceptablePriceImpactBuffer: savedAcceptablePriceImpactBuffer,
          fixedAcceptablePriceImpactBps: acceptablePriceImpactBps,
          externalSwapQuote: undefined,
          leverage,
          triggerPrice,
          marketsInfoData,
          chainId,
          externalSwapQuoteParams: undefined,
          isSetAcceptablePriceImpactEnabled,
        });

        const nextPositionValues = getNextPositionValuesForIncreaseTrade({
          collateralDeltaAmount: increaseAmounts.collateralDeltaAmount,
          collateralDeltaUsd: increaseAmounts.collateralDeltaUsd,
          collateralToken,
          existingPosition,
          indexPrice: increaseAmounts.indexPrice,
          isLong: positionOrder.isLong,
          marketInfo,
          minCollateralUsd,
          showPnlInLeverage: false,
          sizeDeltaInTokens: increaseAmounts.sizeDeltaInTokens,
          sizeDeltaUsd: increaseAmounts.sizeDeltaUsd,
          positionPriceImpactDeltaUsd: increaseAmounts.positionPriceImpactDeltaUsd,
          userReferralInfo,
        });

        if (nextPositionValues.nextLeverage !== undefined) {
          const isMaxLeverageExceeded = getIsMaxLeverageExceeded(
            nextPositionValues.nextLeverage,
            marketInfo,
            positionOrder.isLong,
            increaseAmounts.sizeDeltaUsd
          );

          return {
            isValid: !isMaxLeverageExceeded,
            returnValue: increaseAmounts.sizeDeltaUsd,
          };
        }

        return {
          isValid: false,
          returnValue: increaseAmounts.sizeDeltaUsd,
        };
      }
    );

    if (newSizeDeltaUsd !== undefined) {
      setSizeInputValue(formatAmountFree(substractMaxLeverageSlippage(newSizeDeltaUsd), USD_DECIMALS, 2));
    } else {
      helperToast.error(t`No valid leverage available`);
    }
  }, [
    p.order,
    positionIndexToken,
    fromToken,
    minCollateralUsd,
    maxAllowedLeverage,
    indexTokenAmount,
    findSwapPath,
    existingPosition,
    uiFeeFactor,
    userReferralInfo,
    savedAcceptablePriceImpactBuffer,
    acceptablePriceImpactBps,
    triggerPrice,
    marketsInfoData,
    chainId,
    isSetAcceptablePriceImpactEnabled,
    setSizeInputValue,
  ]);

  const batchParams: BatchOrderTxnParams | undefined = useMemo(() => {
    if (!signer || !tokensData || !marketsInfoData) {
      return undefined;
    }

    if (isSwapOrderType(p.order.orderType)) {
      return {
        createOrderParams: [],
        updateOrderParams: [
          buildUpdateOrderPayload({
            chainId,
            indexTokenAddress: zeroAddress,
            orderKey: p.order.key,
            orderType: p.order.orderType,
            sizeDeltaUsd: 0n,
            triggerPrice: triggerRatio?.ratio ?? p.order.triggerPrice,
            acceptablePrice: 0n,
            minOutputAmount: minOutputAmount ?? p.order.minOutputAmount,
            autoCancel: p.order.autoCancel,
            validFromTime: 0n,
            executionFeeTopUp: additionalExecutionFee?.feeTokenAmount ?? 0n,
          }),
        ],
        cancelOrderParams: [],
      };
    } else {
      const positionOrder = p.order as PositionOrderInfo;
      const nextSizeDeltaUsd = sizeDeltaUsd ?? positionOrder.sizeDeltaUsd;
      const shouldPreserveFullCloseSize =
        isTriggerDecrease &&
        isFullPositionCloseSizeDeltaUsd(positionOrder.sizeDeltaUsd, existingPosition?.sizeInUsd) &&
        existingPosition !== undefined &&
        nextSizeDeltaUsd >= existingPosition.sizeInUsd;

      return {
        createOrderParams: [],
        updateOrderParams: [
          buildUpdateOrderPayload({
            chainId,
            indexTokenAddress: positionOrder.indexToken.address,
            orderKey: p.order.key,
            orderType: p.order.orderType,
            sizeDeltaUsd: shouldPreserveFullCloseSize ? FULL_POSITION_CLOSE_SIZE_DELTA_USD : nextSizeDeltaUsd,
            triggerPrice: triggerPrice ?? positionOrder.triggerPrice,
            acceptablePrice: acceptablePrice ?? positionOrder.acceptablePrice,
            minOutputAmount: minOutputAmount ?? positionOrder.minOutputAmount,
            autoCancel: positionOrder.autoCancel,
            validFromTime: 0n,
            executionFeeTopUp: additionalExecutionFee?.feeTokenAmount ?? 0n,
          }),
        ],
        cancelOrderParams: [],
      };
    }
  }, [
    signer,
    tokensData,
    marketsInfoData,
    p.order,
    existingPosition,
    isTriggerDecrease,
    triggerRatio?.ratio,
    triggerPrice,
    chainId,
    sizeDeltaUsd,
    acceptablePrice,
    minOutputAmount,
    additionalExecutionFee?.feeTokenAmount,
  ]);

  const { expressParams, expressParamsPromise, isMultichainSubmitDisabled } = useExpressOrdersParams({
    orderParams: batchParams,
    label: "Order Editor",
    isGmxAccount: srcChainId !== undefined,
  });

  const networkFee = useMemo(() => {
    if (!additionalExecutionFee) {
      return undefined;
    }

    let feeToken = additionalExecutionFee?.feeToken;
    let feeTokenAmount = additionalExecutionFee?.feeTokenAmount;
    let feeUsd = additionalExecutionFee?.feeUsd;

    const gasPaymentToken = getByKey(tokensData, expressParams?.gasPaymentParams.gasPaymentTokenAddress);

    if (gasPaymentToken && expressParams?.gasPaymentParams.gasPaymentTokenAmount !== undefined) {
      feeToken = gasPaymentToken;
      feeTokenAmount = expressParams?.gasPaymentParams.gasPaymentTokenAmount;
      feeUsd = convertToUsd(feeTokenAmount, feeToken.decimals, gasPaymentToken.prices.minPrice);
    }

    return {
      feeToken,
      feeTokenAmount,
      feeUsd,
    };
  }, [additionalExecutionFee, expressParams, tokensData]);

  const error = useMemo(() => {
    if (isSubmitting) {
      return t`Updating order...`;
    }

    if (isSwapOrderType(p.order.orderType)) {
      if (triggerRatio?.ratio === undefined || triggerRatio?.ratio < 0 || minOutputAmount <= 0) {
        return t`Enter a ratio`;
      }

      if (minOutputAmount === p.order.minOutputAmount) {
        return t`Enter a new ratio or allowed slippage`;
      }

      if (triggerRatio && !isRatioInverted && markRatio && markRatio.ratio < triggerRatio.ratio) {
        return t`Set limit price below mark price`;
      }

      if (triggerRatio && isRatioInverted && markRatio && markRatio.ratio > triggerRatio.ratio) {
        return t`Set limit price above mark price`;
      }

      const expressError = getExpressError({
        expressParams,
        tokensData,
      });

      if (expressError.buttonErrorMessage) {
        return expressError.buttonErrorMessage;
      }

      return;
    }

    return calcSelector(selectOrderEditorPositionOrderError);
  }, [
    isSubmitting,
    p.order.orderType,
    p.order.minOutputAmount,
    calcSelector,
    triggerRatio,
    minOutputAmount,
    isRatioInverted,
    markRatio,
    expressParams,
    tokensData,
  ]);

  const onSubmit = useCallback(async () => {
    if (!batchParams || !signer || !tokensData || !marketsInfoData || !provider) {
      return;
    }

    setIsSubmitting(true);

    const fulfilledExpressParams = await expressParamsPromise;

    const isGmxAccount = srcChainId !== undefined;

    if (
      reportMultichainExpressSubmitError({
        isGmxAccount,
        expressParams: fulfilledExpressParams,
        tokensData,
        actionName: "Update Order",
        collateral: p.order.initialCollateralToken.symbol,
      })
    ) {
      setIsSubmitting(false);
      return;
    }

    const txnPromise = sendBatchOrderTxn({
      chainId,
      signer,
      batchParams,
      expressParams: getExpressParamsForSubmit(fulfilledExpressParams),
      simulationParams: undefined,
      callback: makeOrderTxnCallback({
        actionName: "Update Order",
        collateralSymbol: p.order.initialCollateralToken.symbol,
      }),
      provider,
      isGmxAccount: srcChainId !== undefined,
    });

    if (expressParams?.subaccount) {
      p.onClose();
      setIsSubmitting(false);
      if (market) {
        sendEditOrderEvent({ order: p.order, source: p.source, marketInfo: market });
      }
      return;
    }

    txnPromise
      .then(() => {
        p.onClose();
        if (market) {
          sendEditOrderEvent({ order: p.order, source: p.source, marketInfo: market });
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    batchParams,
    signer,
    tokensData,
    marketsInfoData,
    provider,
    setIsSubmitting,
    expressParamsPromise,
    chainId,
    makeOrderTxnCallback,
    srcChainId,
    expressParams?.subaccount,
    p,
    market,
  ]);

  const submitButtonState = useMemo(() => {
    if (hasOutdatedUi) {
      return {
        text: getPageOutdatedError(),
        disabled: true,
      };
    }

    if (multipleWalletExtensionsChainError.buttonErrorMessage) {
      return {
        text: multipleWalletExtensionsChainError.buttonErrorMessage,
        tooltip: multipleWalletExtensionsChainError.buttonTooltipMessage,
        disabled: true,
      };
    }

    if (
      isTriggerDecrease &&
      isFullPositionCloseSizeDeltaUsd((p.order as PositionOrderInfo).sizeDeltaUsd) &&
      !existingPosition
    ) {
      return {
        text: t`Loading position...`,
        disabled: true,
      };
    }

    if (isMaxLeverageError) {
      return {
        text: t`Max leverage exceeded`,
        tooltip: (
          <>
            <Trans>Order exceeds max leverage. Click to auto-adjust.</Trans>{" "}
            <ExternalLink href="https://docs.gmx.io/docs/trading/order-types/#max-leverage">
              <Trans>Read more</Trans>
            </ExternalLink>
            .
            <br />
            <br />
            <span onClick={detectAndSetAvailableMaxLeverage} className="Tradebox-handle">
              <Trans>Set max leverage</Trans>
            </span>
          </>
        ),
        disabled: true,
      };
    }

    if (isMultichainSubmitDisabled) {
      return {
        text: (
          <>
            {t`Loading network fees…`}
            <SpinnerIcon className="ml-4 animate-spin" />
          </>
        ),
        disabled: true,
      };
    }

    if (error) {
      return {
        text: error,
        disabled: true,
      };
    }

    const orderTypeName = getNameByOrderType(p.order.orderType, p.order.isTwap);

    return {
      text: t`Update ${orderTypeName}`,
      disabled: false,
      onClick: onSubmit,
    };
  }, [
    error,
    multipleWalletExtensionsChainError,
    hasOutdatedUi,
    isMaxLeverageError,
    p.order,
    onSubmit,
    detectAndSetAvailableMaxLeverage,
    isTriggerDecrease,
    existingPosition,
    isMultichainSubmitDisabled,
  ]);

  useKey(
    "Enter",
    () => {
      if (!submitButtonState.disabled) {
        submitButtonState.onClick?.();
      }
    },
    {},
    [submitButtonState]
  );

  useEffect(
    function initValues() {
      if (isInited) return;

      if (isSwapOrderType(p.order.orderType)) {
        const ratio = (p.order as SwapOrderInfo).triggerRatio as TokensRatioAndSlippage;

        if (ratio) {
          setTriggerRatioInputValue(formatAmount(ratio.ratio, USD_DECIMALS, 2));
        }

        if (isLimitSwapOrderType(p.order.orderType)) {
          const totalSwapImpactBps = swapImpactBps >= 0n ? 0n : bigMath.abs(swapImpactBps);
          const defaultSwapImpactBuffer = DEFAULT_ALLOWED_SWAP_SLIPPAGE_BPS + totalSwapImpactBps;

          setDefaultAllowedSwapSlippageBps(bigMath.abs(defaultSwapImpactBuffer));
          setSelectedAllowedSwapSlippageBps(ratio?.allowedSwapSlippageBps, false);
        }
      } else {
        const positionOrder = p.order as PositionOrderInfo;

        if (isTriggerDecrease) {
          const orderSizeDeltaUsd = positionOrder.sizeDeltaUsd ?? 0n;

          if (isFullPositionCloseSizeDeltaUsd(orderSizeDeltaUsd) && !existingPosition) {
            return;
          }

          const displaySizeDeltaUsd = getPositionCloseSizeDeltaUsdForDisplay(
            orderSizeDeltaUsd,
            existingPosition?.sizeInUsd
          );
          const clampedSizeDeltaUsd =
            existingPosition && displaySizeDeltaUsd > existingPosition.sizeInUsd
              ? existingPosition.sizeInUsd
              : displaySizeDeltaUsd;
          closeSize.setFromUsdString(formatAmountFree(clampedSizeDeltaUsd, USD_DECIMALS));
        } else {
          setSizeInputValue(formatAmountFree(positionOrder.sizeDeltaUsd ?? 0n, USD_DECIMALS));
        }
        const price = positionOrder.triggerPrice ?? 0n;
        const decimals = calculateDisplayDecimals(price, USD_DECIMALS, indexToken?.visualMultiplier);

        if (triggerPriceInputValue === "") {
          setTriggerPriceInputValue(
            formatAmount(price, USD_DECIMALS, decimals, undefined, undefined, indexToken?.visualMultiplier)
          );
        }
      }

      setIsInited(true);
    },
    [
      closeSize,
      existingPosition,
      indexToken?.visualMultiplier,
      isInited,
      isTriggerDecrease,
      p.order,
      setDefaultAllowedSwapSlippageBps,
      setSelectedAllowedSwapSlippageBps,
      setSizeInputValue,
      setTriggerPriceInputValue,
      setTriggerRatioInputValue,
      swapImpactBps,
      triggerPriceInputValue,
    ]
  );

  const buttonContent = (
    <Button
      className="w-full"
      variant="primary-action"
      onClick={submitButtonState.onClick}
      disabled={submitButtonState.disabled}
    >
      {submitButtonState.text}
    </Button>
  );

  const button = submitButtonState.tooltip ? (
    <TooltipWithPortal
      position="top"
      handleClassName="w-full"
      tooltipClassName="PositionEditor-tooltip"
      handle={buttonContent}
      isHandlerDisabled
      content={submitButtonState.tooltip}
    />
  ) : (
    buttonContent
  );

  const priceLabel = isLimitDecreaseOrderType(p.order.orderType)
    ? t`Take-Profit price`
    : isStopLossOrderType(p.order.orderType)
      ? t`Stop-Loss price`
      : isStopIncreaseOrderType(p.order.orderType)
        ? t`Stop price`
        : t`Limit price`;

  const positionSize = existingPosition?.sizeInUsd;

  const sizeUsd = parseValue(sizeInputValue || "0", USD_DECIMALS) ?? 0n;

  const handleBack = () => {
    if (p.onBack) {
      p.onBack();
      return;
    }

    p.onClose();
  };

  const editorTitle = useMemo(() => {
    if (isSwapOrderType(p.order.orderType)) {
      const fromSymbol = p.order.initialCollateralToken.symbol;
      const toSymbol = p.order.targetCollateralToken.symbol;
      return t`Edit Limit Swap: ${fromSymbol} to ${toSymbol}`;
    }

    const longShortText = p.order.isLong ? t`Long` : t`Short`;
    const tokenSymbol = indexToken?.symbol ?? "";
    const suffix = `${tokenSymbol} ${longShortText}`;

    if (isLimitDecreaseOrderType(p.order.orderType)) {
      return t`Edit Take-Profit: ${suffix}`;
    }
    if (isStopLossOrderType(p.order.orderType)) {
      return t`Edit Stop-Loss: ${suffix}`;
    }
    if (isStopIncreaseOrderType(p.order.orderType)) {
      return t`Edit Stop Market: ${suffix}`;
    }
    if (isLimitIncreaseOrderType(p.order.orderType)) {
      return t`Edit Limit Increase: ${suffix}`;
    }

    return t`Edit ${p.order.title}`;
  }, [
    p.order.orderType,
    p.order.isLong,
    p.order.title,
    p.order.initialCollateralToken?.symbol,
    p.order.targetCollateralToken?.symbol,
    indexToken?.symbol,
  ]);

  return (
    <div className="PositionEditor">
      <Modal
        className="PositionSeller-modal"
        isVisible={true}
        setIsVisible={p.onClose}
        label={editorTitle}
        contentPadding={false}
        onBack={p.onBack ? handleBack : undefined}
      >
        <div className="flex flex-col gap-4 px-20 py-16">
          {!isSwapOrderType(p.order.orderType) && (
            <>
              <BuyInputSection
                topLeftLabel={isTriggerDecrease ? t`Close` : t`Size`}
                inputValue={isTriggerDecrease ? closeSize.closeSizeInput : sizeInputValue}
                onInputValueChange={
                  isTriggerDecrease ? closeSize.handleInputChange : (e) => setSizeInputValue(e.target.value)
                }
                bottomLeftValue={isTriggerDecrease ? formatUsd(sizeUsd) : undefined}
                bottomRightLabel={isTriggerDecrease && positionSize !== undefined ? t`Max` : undefined}
                bottomRightValue={isTriggerDecrease ? formatUsdPrice(positionSize) : undefined}
                onClickMax={
                  isTriggerDecrease && positionSize !== undefined && positionSize > 0 && sizeUsd !== positionSize
                    ? closeSize.setMaxCloseSize
                    : undefined
                }
                maxDecimals={
                  isTriggerDecrease && closeSize.showSizeInTokens ? positionIndexToken?.decimals ?? 18 : USD_DECIMALS
                }
              >
                {isTriggerDecrease ? (
                  <span className="cursor-pointer select-none" onClick={closeSize.handleSizeToggle}>
                    {closeSize.closeSizeLabel}
                  </span>
                ) : (
                  t`USD`
                )}
              </BuyInputSection>

              <BuyInputSection
                topLeftLabel={priceLabel}
                topRightLabel={t`Mark`}
                topRightValue={formatUsdPrice(markPrice, {
                  visualMultiplier: indexToken?.visualMultiplier,
                })}
                onClickTopRightLabel={() =>
                  setTriggerPriceInputValue(
                    formatAmount(
                      markPrice,
                      USD_DECIMALS,
                      calculateDisplayDecimals(markPrice, USD_DECIMALS, indexToken?.visualMultiplier),
                      undefined,
                      undefined,
                      indexToken?.visualMultiplier
                    )
                  )
                }
                inputValue={triggerPriceInputValue}
                onInputValueChange={(e) => setTriggerPriceInputValue(e.target.value)}
                maxDecimals={USD_DECIMALS}
              >
                {t`USD`}
              </BuyInputSection>

              {isTriggerDecrease && positionSize !== undefined && positionSize > 0n && (
                <MarginPercentageSlider value={closeSize.closePercentage} onChange={closeSize.handleSliderChange} />
              )}
            </>
          )}

          {isSwapOrderType(p.order.orderType) && (
            <>
              {triggerRatio && (
                <BuyInputSection
                  topLeftLabel={t`Limit price`}
                  topRightLabel={t`Mark`}
                  topRightValue={formatAmount(markRatio?.ratio, USD_DECIMALS, 4)}
                  onClickTopRightLabel={() => {
                    setTriggerRatioInputValue(formatAmount(markRatio?.ratio, USD_DECIMALS, 10));
                  }}
                  inputValue={triggerRatioInputValue}
                  onInputValueChange={(e) => {
                    setTriggerRatioInputValue(e.target.value);
                  }}
                  maxDecimals={USD_DECIMALS}
                >
                  <Trans>
                    {triggerRatio.smallestToken.symbol} per {triggerRatio.largestToken.symbol}
                  </Trans>
                </BuyInputSection>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-14 px-20 pb-16">
          {(isLimitIncreaseOrderType(p.order.orderType) || isStopIncreaseOrderType(p.order.orderType)) && (
            <SyntheticsInfoRow
              label={t`Leverage`}
              value={
                <ValueTransition
                  from={formatLeverage(existingPosition?.leverage)}
                  to={formatLeverage(nextPositionValuesForIncrease?.nextLeverage) ?? "-"}
                />
              }
            />
          )}

          {!isSwapOrderType(p.order.orderType) &&
            !isStopLossOrderType(p.order.orderType) &&
            !isStopIncreaseOrderType(p.order.orderType) &&
            isSetAcceptablePriceImpactEnabled && (
              <AcceptablePriceImpactInputRow
                acceptablePriceImpactBps={acceptablePriceImpactBps}
                initialPriceImpactFeeBps={initialAcceptablePriceImpactBps}
                recommendedAcceptablePriceImpactBps={recommendedAcceptablePriceImpactBps}
                setAcceptablePriceImpactBps={setAcceptablePriceImpactBps}
                priceImpactFeeBps={priceImpactFeeBps}
              />
            )}
          {!isSwapOrderType(p.order.orderType) && (
            <>
              {isSetAcceptablePriceImpactEnabled && (
                <SyntheticsInfoRow
                  label={t`Acceptable price`}
                  value={formatAcceptablePrice(acceptablePrice, {
                    visualMultiplier: indexToken?.visualMultiplier,
                  })}
                />
              )}

              {existingPosition && (
                <SyntheticsInfoRow
                  label={t`Liquidation price`}
                  value={formatLiquidationPrice(existingPosition.liquidationPrice, {
                    visualMultiplier: indexToken?.visualMultiplier,
                  })}
                />
              )}
            </>
          )}

          {networkFee && (
            <SyntheticsInfoRow
              label={t`Fees`}
              value={
                <TooltipWithPortal
                  position="top-end"
                  tooltipClassName="PositionEditor-fees-tooltip"
                  handle={formatDeltaUsd(networkFee.feeUsd === undefined ? undefined : networkFee.feeUsd * -1n)}
                  renderContent={() => (
                    <>
                      <StatsTooltipRow
                        label={<div className="text-typography-primary">{t`Network fee`}:</div>}
                        value={formatTokenAmountWithUsd(
                          networkFee.feeTokenAmount * -1n,
                          networkFee.feeUsd === undefined ? undefined : networkFee.feeUsd * -1n,
                          networkFee.feeToken.symbol,
                          networkFee.feeToken.decimals,
                          {
                            displayDecimals: 5,
                            isStable: networkFee.feeToken.isStable,
                          }
                        )}
                        showDollar={false}
                      />
                      <br />
                      <div className="text-typography-primary">
                        <Trans>Network fees increased. Additional fee required.</Trans>
                      </div>
                    </>
                  )}
                />
              }
            />
          )}

          {isSwapOrderType(p.order.orderType) && (
            <>
              <AllowedSwapSlippageInputRow
                notAvailable={false}
                totalSwapImpactBps={swapImpactBps}
                allowedSwapSlippageBps={selectedAllowedSwapSlippageBps}
                recommendedAllowedSwapSlippageBps={defaultAllowedSwapSlippageBps}
                setAllowedSwapSlippageBps={setSelectedAllowedSwapSlippageBps}
              />
              <div className="h-1 bg-slate-600" />
              <SyntheticsInfoRow
                label={t`Min receive`}
                value={formatBalanceAmount(
                  minOutputAmount,
                  p.order.targetCollateralToken.decimals,
                  p.order.targetCollateralToken.symbol,
                  { isStable: p.order.targetCollateralToken.isStable }
                )}
              />
            </>
          )}

          <ExpressTradingWarningCard
            expressParams={expressParams}
            payTokenAddress={undefined}
            isWrapOrUnwrap={false}
            isGmxAccount={srcChainId !== undefined}
            onAfterAction={p.onClose}
          />

          {button}
        </div>
      </Modal>
    </div>
  );
}
